import { useCallback, useState } from "react";

import { Button, Modal, Stack, Typography } from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { EvotruxDivider } from "components/EvotruxDivider";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { UnifiedCustomerSelectorContainer } from "components/Shipper/components/CreateShipmentForm/components/UnifiedCustomerSelector";
import { useShipperShipmentPageContext, useWriterComponentState } from "hooks";
import { MODAL_STYLE } from "layouts/constants";

import { ClientAppEvent } from "../../../../../../../../constants";

import type { CustomerSelectionResult } from "components/Shipper/components/CreateShipmentForm/components/UnifiedCustomerSelector/types";

interface Props {
  onModalClose: VoidFunction;
  modalOpen: boolean;
}

export function AddCustomersToShipment(props: Props) {
  const { shipment, onRefresh } = useShipperShipmentPageContext();
  const posthog = usePostHog();
  const [customerSelection, setCustomerSelection] =
    useState<CustomerSelectionResult>({
      customerRoleIds: [],
      externalCustomerIds: [],
      customerInviteEmails: [],
    });

  // Get existing customer emails to prevent duplicates
  const existingCustomerEmails = shipment?.supplier_shipment_info
    ? [
        ...shipment.supplier_shipment_info.customer_roles.map(
          (role) => role.user.email,
        ),
        ...shipment.supplier_shipment_info.external_customers.map(
          (customer) => customer.email,
        ),
      ]
    : [];

  // Get existing customer role IDs to prevent duplicates
  const existingCustomerRoleIds =
    shipment?.supplier_shipment_info?.customer_roles.map((role) => role.id) ??
    [];

  // Get existing external customer IDs to prevent duplicates
  const existingExternalCustomerIds =
    shipment?.supplier_shipment_info?.external_customers.map(
      (customer) => customer.id,
    ) ?? [];

  const { modalOpen, onModalClose } = props;

  const { loading, write, error } = useWriterComponentState({
    errorMessage:
      "Failed to add customers to shipment. Please contact us if the issue persists",
    writeConfig: {
      method: "patch",
      url: shipment ? `/shipments/${shipment.info.id}/update` : "/",
      body: {
        supplier_shipment_info: {
          customer_role_ids: customerSelection.customerRoleIds,
          external_customer_ids: customerSelection.externalCustomerIds,
          customer_invite_emails: customerSelection.customerInviteEmails,
        },
      },
    },
  });

  const onCustomerSelectionChange = useCallback(
    (data: CustomerSelectionResult) => {
      setCustomerSelection(data);
    },
    [],
  );

  const submit = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_CUSTOMERS_ADDED, {
        internal_customers_count: customerSelection.customerRoleIds.length,
        external_customers_count: customerSelection.externalCustomerIds.length,
        email_invites_count: customerSelection.customerInviteEmails.length,
        shipment_id: shipment?.info.id,
        customer_role_ids: customerSelection.customerRoleIds,
        external_customer_ids: customerSelection.externalCustomerIds,
        customer_invite_emails: customerSelection.customerInviteEmails,
      });
      onRefresh?.();
      onModalClose();
      setCustomerSelection({
        customerRoleIds: [],
        externalCustomerIds: [],
        customerInviteEmails: [],
      });
    });
  };

  const hasCustomersSelected =
    customerSelection.customerRoleIds.length > 0 ||
    customerSelection.externalCustomerIds.length > 0 ||
    customerSelection.customerInviteEmails.length > 0;

  return (
    <Modal
      open={modalOpen}
      onClose={onModalClose}
      aria-labelledby="add-customers-modal-title"
      aria-describedby="add-customers-modal-description"
    >
      <Stack sx={MODAL_STYLE} spacing={2}>
        <Typography id="add-customers-modal-title" variant="h6" component="h2">
          Add Customers to Shipment
        </Typography>
        <EvotruxDivider />

        <Typography variant="body2" color="text.secondary">
          You can also share a public tracking link with anyone — no account
          required.
        </Typography>

        <LoadingWrappedNode loading={loading}>
          <UnifiedCustomerSelectorContainer
            onCustomerSelectionChange={onCustomerSelectionChange}
            initialCustomerRoleIds={[]}
            initialExternalCustomerIds={[]}
            initialCustomerInviteEmails={[]}
            excludeCustomerRoleIds={existingCustomerRoleIds}
            excludeExternalCustomerIds={existingExternalCustomerIds}
            excludeCustomerEmails={existingCustomerEmails}
            isInAddCustomersToShipmentModal
            autoFocus
          />

          {error && <ErrorTypography error={error} />}

          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={onModalClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={submit}
              disabled={loading || !hasCustomersSelected}
            >
              Add Customers
            </Button>
          </Stack>
        </LoadingWrappedNode>
      </Stack>
    </Modal>
  );
}
